/**
 * R-132-ANTI-AI-VOICE — lint for AI-typical voice patterns.
 *
 * Checks for:
 * - Em-dash overuse (the prompt budgets 1–2 per section)
 * - Inflated / slop words (robust, leverage, delve, tapestry, testament, …)
 * - Filler transitions (it's worth noting, importantly, notably)
 * - Formulaic ADJECTIVE triads ("efficient, robust, and scalable")
 * - Hedging (can be considered, might be possible)
 *
 * ─── Coverage (P2-GOLD P4 / A1) ───────────────────────────────────────
 *
 * `auditAiVoice` now scans the FULL reader-facing prose of the report — the
 * base / typed / fallback section bodies a reader actually sees — via the
 * shared `extractSections` extractor, not just the (prod-default-OFF)
 * `editorialNarrative` layer. Previously the detector saw only
 * `editorialNarrative`, which is flag-OFF in prod, so it policed nothing
 * shipped. `extractSections` already folds the editorial layer in as one
 * section, so there is no double-count.
 *
 * ─── Discipline ───────────────────────────────────────────────────────
 *
 *  • PURE. No I/O, no DOM, never mutates inputs, never throws on bad data.
 *  • Each finding is emitted at severity `'warn'` here. Any escalation to
 *    `'block'` (+ regenerate-once) is the WIRE layer's job, gated behind the
 *    `REPORT_VOICE_ENFORCE` flag (default 'warn') — the pure detector stays
 *    advisory so it can never, on its own, fail a generation.
 */

import { extractSections } from './reportTextExtract';

export interface AiVoiceFinding {
  detector: 'anti-ai-voice';
  sectionId: string;
  severity: 'warn';
  message: string;
  matchedPhrases: string[];
}

/**
 * Inflated / "AI slop" vocabulary. Expanded (A2) with the common prod
 * tells the editorial prompt explicitly bans: tapestry, delve, underscore,
 * navigate, realm, testament, crucial, pivotal, etc. Matched whole-word,
 * case-insensitive. Kept to genuinely load-bearing slop so a legitimate
 * domain use (e.g. "robust dataset") is a WARN, never a block on its own.
 */
const INFLATED_WORDS = [
  // original set
  'robust',
  'leverage',
  'delve',
  'comprehensive',
  'synergy',
  'paradigm',
  'seamless',
  'spearhead',
  'pivotal',
  'multifaceted',
  // A2 — common prod slop the prompt bans
  'tapestry',
  'underscore',
  'underscores',
  'underscoring',
  'navigate',
  'navigating',
  'realm',
  'testament',
  'crucial',
  'vital',
  'myriad',
  'plethora',
  'bustling',
  'intricate',
  'meticulous',
  'meticulously',
  'unwavering',
  'unparalleled',
  'cutting-edge',
  'game-changing',
  'game-changer',
  'holistic',
  'seamlessly',
  'foster',
  'fostering',
  'harness',
  'harnessing',
  'elevate',
  'embark',
  'unlock',
  'unlocking',
  'showcase',
  'showcasing',
  'reimagine',
];

const FILLER_TRANSITIONS = [
  "it's worth noting",
  'it is worth noting',
  'importantly',
  'notably',
  'first and foremost',
  'moreover',
  'furthermore',
  'it should be noted',
  'in conclusion',
  'to summarize',
  'in summary',
  'in today’s world',
  "in today's world",
  'when it comes to',
  'needless to say',
  'at the end of the day',
];

const HEDGING = [
  'can be considered',
  'might be possible',
  'could potentially',
  'it appears that',
  'seems to be',
];

/**
 * The em-dash budget. The editorial prompt asks for AT MOST 1–2 em-dashes per
 * section, so 3+ reads as the AI tell. A2 tightens the prior `> 3` threshold to
 * `> 2` to match the prompt's stated budget.
 */
const EM_DASH_MAX = 2;

/**
 * Formulaic ADJECTIVE triad. A2 narrows the prior naive `(\w+), (\w+), and
 * (\w+)` (which flagged ANY three-item list, including legitimate enumerations
 * of nouns / proper nouns / figures) to a triad of lowercase alphabetic words
 * (the classic AI cadence "efficient, robust, and scalable"). Requiring all
 * three members to be lowercase letters excludes numeric lists, citations,
 * capitalised proper-noun lists, and unit lists — high precision, favouring
 * false-negatives over false-positives.
 */
const ADJ_TRIAD_REGEX = /\b([a-z]{4,}),\s+([a-z]{4,}),\s+and\s+([a-z]{4,})\b/g;

export function detectAiVoice(sectionText: string, sectionId: string): AiVoiceFinding[] {
  const text = sectionText.trim();
  if (!text) return [];

  const textLower = text.toLowerCase();
  const matchedPhrases: string[] = [];

  // Em-dash overuse (A2 — budget 1–2 per section).
  const emDashCount = (text.match(/—/g) || []).length;
  if (emDashCount > EM_DASH_MAX) {
    matchedPhrases.push('em-dash overuse');
  }

  // Inflated / slop words (whole-word, case-insensitive). Hyphenated entries
  // (e.g. cutting-edge) need a custom boundary since `\b` sits at the hyphen.
  for (const word of INFLATED_WORDS) {
    const escaped = word.replace(/[-]/g, '\\-');
    const regex = word.includes('-')
      ? new RegExp(`(?<![\\w-])${escaped}(?![\\w-])`, 'i')
      : new RegExp(`\\b${escaped}\\b`, 'i');
    if (regex.test(text)) {
      matchedPhrases.push(word);
    }
  }

  // Filler transitions.
  for (const filler of FILLER_TRANSITIONS) {
    if (textLower.includes(filler)) {
      matchedPhrases.push(filler);
    }
  }

  // Hedging.
  for (const hedge of HEDGING) {
    if (textLower.includes(hedge)) {
      matchedPhrases.push(hedge);
    }
  }

  // Formulaic adjective triad (A2 — narrowed).
  let match: RegExpExecArray | null;
  ADJ_TRIAD_REGEX.lastIndex = 0;
  while ((match = ADJ_TRIAD_REGEX.exec(text)) !== null) {
    matchedPhrases.push(`formulaic triad: ${match[0]}`);
  }

  if (matchedPhrases.length > 0) {
    return [
      {
        detector: 'anti-ai-voice',
        sectionId,
        severity: 'warn', // The governance intends for this to be a warn finding
        message: `Section "${sectionId}" contains AI-typical voice patterns: ${matchedPhrases.join(', ')}.`,
        matchedPhrases,
      },
    ];
  }

  return [];
}

/**
 * Build the regenerate-once repair-overlay text from accumulated voice
 * findings (Part A3). Mirrors `buildGroundingRepairPrompt` — re-states the
 * offending AI-voice patterns + the editorial voice rules so the retry reads
 * human. Pure; returns `''` when there are no findings (so the caller can
 * skip the retry). NEVER throws.
 */
export function buildVoiceRepairPrompt(findings: AiVoiceFinding[]): string {
  if (findings.length === 0) return '';
  // De-dup the matched phrases across sections for a compact instruction.
  const phrases = new Set<string>();
  for (const f of findings) {
    for (const p of f.matchedPhrases) phrases.add(p);
  }
  const lines: string[] = [
    '## VOICE REPAIR — your previous draft read as AI-generated',
    'Rewrite in a plain, human, analyst voice. Do NOT change any facts, numbers, or citations — only the prose voice. Specifically remove / avoid these patterns:',
    ...[...phrases].slice(0, 40).map((p) => `  - ${p}`),
    'Rules: at most 1–2 em-dashes per section; no inflated/“slop” words; no filler transitions; no formulaic adjective triads; no hedging. Keep every figure and [Ref N] exactly as-is.',
  ];
  return lines.join('\n');
}

/**
 * Audit the WHOLE report's reader-facing prose for AI-typical voice (A1).
 *
 * Scans every section body a reader actually sees (base / typed / fallback
 * narrative + the editorial layer when present) via the shared
 * `extractSections` extractor — never throws; a malformed report degrades to
 * no findings. Each finding is severity `'warn'`; the wire-layer escalation
 * (Part A3, `REPORT_VOICE_ENFORCE`) decides whether to upgrade.
 */
export function auditAiVoice(report: import('../../types/siteRag').SiteRagReport): AiVoiceFinding[] {
  const findings: AiVoiceFinding[] = [];
  try {
    for (const { id, content } of extractSections(report)) {
      if (typeof content === 'string' && content.trim()) {
        findings.push(...detectAiVoice(content, id));
      }
    }
  } catch {
    // PURE + fail-open: a malformed report must never crash the audit.
    return findings;
  }
  return findings;
}
